import type { ReactNode } from "react";
import { Link } from "react-router";
import { Layout, PageHeader } from "@/components/layout/Layout";
import { preferredCurrencies } from "@/domain/money";

const SUPPORT_EMAIL = "info@trade-r.com";

/**
 * FAQs.
 *
 * The 2020 page had genuine content, and most of it is carried over. Four
 * things changed because the answers did:
 *
 * 1. "Trade-r only supports adding and deleting trades. A future version will
 *    feature trade edits." — editing open trades shipped in Phase 4c.
 * 2. The preferred-currency answer now states that figures are captured at
 *    write time and are not restated, which the 2020 app did not have to say
 *    because it recalculated them on every change. It no longer can.
 * 3. The 2020 page closed by soliciting Basic Attention Token and Bitcoin
 *    donations, and trailed off mid-sentence — `send some Bitcoin to the
 *    following address: " "` — because the address was interpolated from an
 *    env var that was stripped in Phase 0. Donations are dropped from the
 *    rebuild entirely, so the question is gone rather than rewritten.
 * 4. Two answers are new, covering questions the app's own behaviour raises:
 *    what counts as a win, and where the prices come from.
 */
function Question({ q, children }: { q: string; children: ReactNode }) {
  return (
    <section className="border-t-2 border-ink py-8 first:border-t-0 first:pt-0">
      <h2 className="text-xl font-black tracking-tight">{q}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed">{children}</div>
    </section>
  );
}

export function Faqs() {
  return (
    <Layout title="FAQs">
      <PageHeader eyebrow="Frequently asked" title="FAQs" />

      <div className="mx-auto max-w-2xl px-6 py-12">
        <Question q="What is a preferred currency?">
          <p>
            Every trade is quoted in its own pair — BTC/USD, ADA/BTC, ETH/USDC. Profits in
            different quote currencies cannot be added together, so Trade-r also stores each
            profit restated in one currency of your choosing. That is what makes a portfolio
            total, a win rate, or a "best coin" possible at all.
          </p>
          <p>Currently supported:</p>
          <ul className="ml-5 list-disc space-y-1">
            {preferredCurrencies.map((currency) => (
              <li key={currency.value} className="figure">
                {currency.unit} {currency.label}
              </li>
            ))}
          </ul>
          <div className="notice-warn mt-4">
            <p className="font-medium">Pick it before you start recording trades.</p>
            <p className="mt-2">
              The converted figure is captured at the moment a trade is written and cannot be
              filled in later — historical exchange rates are not available on the free data
              plans Trade-r runs on. Changing your preferred currency affects new trades only; it
              does not restate past ones.
            </p>
          </div>
          <p>
            Example: Sam holds an ADA/BTC trade and several BTC/USD trades, with USD as her
            preferred currency. When she closes the ADA/BTC trade for 0.05 BTC, Trade-r stores
            both that figure and its USD equivalent — so the ADA trade and the BTC trades can
            finally be compared.
          </p>
        </Question>

        <Question q="What counts as a win?">
          <p>
            Reaching your profit target. Not merely closing above your buy price.
          </p>
          <p>
            This is the one place Trade-r is opinionated. A trade you closed early for a small
            gain is recorded as having ended <em>between</em> your two lines, and it does not
            count toward your win rate. That is not an accounting quirk — a plan you write and
            then abandon is the specific habit this app exists to break. The{" "}
            <Link to="/performance" className="underline">
              performance screen
            </Link>{" "}
            reports it directly.
          </p>
        </Question>

        <Question q="Does Trade-r connect to my exchange?">
          <p>
            No. It never holds your funds, never moves them, and never asks for exchange API keys
            or wallet access. You buy wherever you already buy, then record the trade here. The
            only thing Trade-r reads from the outside world is public price data.
          </p>
        </Question>

        <Question q="Where do the prices come from?">
          <p>
            Current and historical prices come from{" "}
            <a
              href="https://defillama.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              DefiLlama
            </a>
            , which is free, keyless, and goes back further than the alternatives. Coin and
            exchange reference data comes from{" "}
            <a
              href="https://www.coingecko.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              CoinGecko
            </a>
            .
          </p>
          <p>
            Prices are quoted in USD and other pairs are derived from them, so a figure may differ
            slightly from your exchange's. Both services are free tiers with no guarantees — if a
            lookup fails, Trade-r shows your trades without a live price rather than showing you
            a wrong one.
          </p>
        </Question>

        <Question q="Can I edit a trade?">
          <p>
            Yes, while it is open — the entry details and both targets. This was not possible in
            the original version of Trade-r.
          </p>
          <p>
            A closed trade cannot be edited, deliberately. Its realised profit was calculated
            against prices from the day it closed, and quietly recomputing that from today's data
            would produce a number that never happened. Delete it and record it again if it is
            wrong.
          </p>
        </Question>

        <Question q="My trade looks wrong. What can I do?">
          <p>
            Delete it and add it again. If the problem comes back, please report it — that is a
            bug, not you.
          </p>
        </Question>

        <Question q="How do I report a bug?">
          <p>
            Email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>{" "}
            with as much as you can give:
          </p>
          <ul className="ml-5 list-disc space-y-1">
            <li>Which page it happened on</li>
            <li>The email address on your account, so it can be traced</li>
            <li>Which trade the problem showed up in</li>
          </ul>
        </Question>

      </div>
    </Layout>
  );
}
